// Shared visual primitives for the editorial marketing pages
// (HomePage, ProductsPage, UseCasesPage, PartnersPage).
//
// Strict three-colour edition — Navy / Gold / Green only (on neutral white).
// Pastel surfaces have been removed in favour of a high-contrast tier-1
// SaaS aesthetic: white surfaces with navy text, gold accents, green status.
import React from "react";
import { motion } from "framer-motion";

// Brand tokens reused across the marketing site.
export const NAVY  = "#0B1E3F"; // primary text + dark surfaces
export const GOLD  = "#C8A24B"; // accent / italics / CTAs
export const GREEN = "#1F6B4A"; // status / success badges
// Compatibility re-exports so older imports continue to resolve to brand ink.
export const INK = NAVY;

export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

export const Reveal = ({ children, className = "", delay = 0, ...rest }) => (
  <motion.div className={className}
    initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
    variants={{
      hidden: { opacity: 0, y: 40 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] } },
    }}
    {...rest}>
    {children}
  </motion.div>
);

export const Overline = ({ children, tone = GOLD }) => (
  <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold"
       style={{ color: tone }}>
    {children}
  </div>
);

// Editorial hero — white surface, navy ink, gold italic, sparing green dot.
export const EditorialHero = ({ eyebrow, title, italic, sub, children }) => (
  <section className="relative overflow-hidden py-24 sm:py-32 border-b bg-white"
           style={{ borderColor: `${NAVY}10` }}>
    {/* gold + green orbs for depth, kept very low opacity */}
    <div className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full pointer-events-none"
         style={{ background: `radial-gradient(circle, ${GOLD}1F, transparent 60%)`, filter: "blur(60px)" }} />
    <div className="absolute -bottom-32 -right-24 w-[560px] h-[560px] rounded-full pointer-events-none"
         style={{ background: `radial-gradient(circle, ${GREEN}14, transparent 70%)`, filter: "blur(80px)" }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl">
        <motion.div variants={fadeUp}>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
            <Overline>{eyebrow}</Overline>
          </span>
        </motion.div>
        <motion.h1 variants={fadeUp}
          className="font-serif text-[clamp(2.4rem,6vw,4.8rem)] font-light tracking-tighter leading-[1.04] mt-6"
          style={{ color: NAVY }}>
          {title}{" "}
          <span className="italic" style={{ color: GOLD }}>{italic}</span>
        </motion.h1>
        {sub && (
          <motion.p variants={fadeUp} className="mt-8 text-lg sm:text-xl font-light leading-relaxed max-w-2xl"
                    style={{ color: `${NAVY}B3` }}>
            {sub}
          </motion.p>
        )}
        {children && <motion.div variants={fadeUp} className="mt-10">{children}</motion.div>}
      </motion.div>
    </div>
  </section>
);

export const SectionHeader = ({ eyebrow, title, italic, className = "max-w-3xl mb-16" }) => (
  <Reveal className={className}>
    <Overline>{eyebrow}</Overline>
    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05]"
        style={{ color: NAVY }}>
      {title}{italic && " "}
      {italic && <span className="italic" style={{ color: GOLD }}>{italic}</span>}
    </h2>
  </Reveal>
);

// White paper card with a hair-line navy border. Hover lifts subtly.
export const GlassCard = ({ children, className = "", hover = true, testid }) => (
  <div data-testid={testid}
    className={`rounded-2xl p-8 bg-white ${hover ? "transition-all hover:-translate-y-1" : ""} ${className}`}
    style={{ border: `1px solid ${NAVY}15`,
              boxShadow: "0 6px 18px rgba(11,30,63,0.05)" }}>
    {children}
  </div>
);
